import { cva, type VariantProps } from 'class-variance-authority'
import { Switch as AriaSwitch, type SwitchProps as AriaSwitchProps } from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Switch}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @atlas/tokens
 * (brand). No hardcoded values.
 *
 * @remarks
 * - Supports three sizes: sm, md (default), lg
 * - Focus ring uses the brand primary color
 * - Selected state uses the brand primary background
 * - Animated toggle transition
 *
 * @example
 * ```tsx
 * const classes = switchVariants({ size: 'md' })
 * // Returns: "relative inline-flex h-6 w-11 rounded-ds-full ..."
 * ```
 */
export const switchVariants = cva(
	'relative inline-flex shrink-0 cursor-pointer rounded-ds-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 bg-ds-border group-data-[selected]:bg-ds-primary',
	{
		variants: {
			size: {
				sm: 'h-4 w-8',
				md: 'h-6 w-11',
				lg: 'h-8 w-14',
			},
		},
		defaultVariants: { size: 'md' },
	},
)

/**
 * Props for the {@link Switch} component.
 *
 * Extends React Aria's SwitchProps with CVA size variants.
 *
 * @property size - Switch size: 'sm' | 'md' (default) | 'lg'
 * @property className - Optional additional Tailwind classes
 * @property children - Label text
 * @property isDisabled - Disabled state (React Aria pattern)
 * @property isSelected - Controlled checked state
 *
 * @example
 * ```tsx
 * <Switch size="md" isSelected={true}>
 *   Enable notifications
 * </Switch>
 * ```
 */
export interface SwitchProps extends Omit<AriaSwitchProps, 'className'>, VariantProps<typeof switchVariants> {
	className?: string
}

/**
 * Accessible toggle switch primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Keyboard navigation (Space to toggle), focus management,
 *   and ARIA attributes provided by React Aria's `<Switch>` primitive.
 * - **Styling:** All colors, spacing, and sizing use design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Sizes:** Small (h-4 w-8), medium (h-6 w-11, default), large (h-8 w-14).
 * - **Animation:** Smooth slide transition when toggled.
 * - **Focus indicator:** 2px ring in the brand primary color.
 * - **On state:** the brand primary color background.
 *
 * @example Basic usage
 * ```tsx
 * import { Switch } from '@atlas/ui-primitives'
 *
 * function MyForm() {
 *   return (
 *     <form>
 *       <Switch>Enable notifications</Switch>
 *       <Switch isRequired>Accept terms</Switch>
 *     </form>
 *   )
 * }
 * ```
 *
 * @example With controlled state
 * ```tsx
 * const [isEnabled, setIsEnabled] = useState(false)
 *
 * <Switch
 *   isSelected={isEnabled}
 *   onChange={setIsEnabled}
 * >
 *   Dark mode
 * </Switch>
 * ```
 *
 * @see {@link switchVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/switch/switch.stories.tsx for visual examples
 */
export function Switch({ size, className, children, ...props }: SwitchProps) {
	const thumbSizes = {
		sm: 'h-3 w-3 group-data-[selected]:translate-x-4',
		md: 'h-5 w-5 group-data-[selected]:translate-x-5',
		lg: 'h-7 w-7 group-data-[selected]:translate-x-6',
	}

	return (
		// `relative`: React Aria renders the real <input> inside a visually-hidden
		// `position: absolute` span. Without a positioned ancestor its containing block
		// is the initial containing block, so its box lands at document coordinates
		// matching its static position — deep inside any scrolled container. That
		// stretches the DOCUMENT's scroll height, and focusing it on click scrolls the
		// window to the phantom offset, opening dead space below the app shell.
		<AriaSwitch {...props} className="relative flex items-center gap-2 group">
			<div className={cn(switchVariants({ size }), className)}>
				<span
					className={cn(
						'pointer-events-none inline-block rounded-ds-full bg-ds-background shadow-ds-lg ring-0 transition-transform translate-x-0',
						thumbSizes[size ?? 'md'],
					)}
				/>
			</div>
			{children && <span className="text-ds-base text-ds-text select-none">{children as React.ReactNode}</span>}
		</AriaSwitch>
	)
}
